import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, User, Newspaper } from "lucide-react";
import AnimatedSection from "../../components/ui/AnimatedSection";
import { newsService } from "@/services/news.service";
import { NewsArticle } from "@/types/news.types";

export default function NewsBentoGrid() {
    const [news, setNews] = useState<NewsArticle[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const data = await newsService.getLatestNews();
                setNews(data);
            } catch (err) {
                setError("Failed to load news");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchNews();
    }, []);

    if (loading) return null; // Or a skeleton if preferred, but existing design usually prefers clean loading or nothing
    if (error) return null;
    if (news.length === 0) return null;

    return (
        <section className="section-padding bg-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#d4b86a]/30 to-transparent" />
            <div className="container-custom">
                <AnimatedSection className="text-center mb-12">
                    <span className="badge mb-4">Latest Updates</span>
                    <h2 className="text-gray-900 mb-4">
                        Insights & <span className="gradient-text">News</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        Stay updated with the latest trends and insights from the financial world.
                    </p>
                </AnimatedSection>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[300px]">
                    {news.map((item, index) => {
                        // First item spans 2 columns and 2 rows (large hero)
                        // Second item spans 2 columns and 1 row (wide)
                        // Others are 1x1
                        const isLarge = index === 0;
                        const isWide = index === 1;

                        let gridClass = "";
                        if (isLarge) gridClass = "md:col-span-2 md:row-span-2";
                        else if (isWide) gridClass = "md:col-span-2 md:row-span-1";
                        else gridClass = "col-span-1 row-span-1";

                        return (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className={`group relative rounded-2xl overflow-hidden bg-gray-100 ${gridClass}`}
                            >
                                <Link to={`/news/${item.slug}`} className="block h-full w-full">
                                    {/* Background Image */}
                                    <div className="absolute inset-0">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                    </div>

                                    {/* Content */}
                                    <div className="absolute bottom-0 left-0 p-6 w-full">
                                        <div className="flex items-center space-x-3 text-xs text-white/80 mb-2">
                                            <span className="flex items-center">
                                                <Calendar className="w-3 h-3 mr-1" />
                                                {new Date(item.date).toLocaleDateString()}
                                            </span>
                                            <span className="bg-[#c9a84c] text-white px-2 py-0.5 rounded text-[10px] font-semibold uppercase">
                                                {item.category}
                                            </span>
                                        </div>

                                        <h3 className={`font-display font-medium text-white mb-2 group-hover:text-[#c9a84c] transition-colors ${isLarge ? 'text-2xl' : 'text-lg'}`}>
                                            {item.title}
                                        </h3>

                                        {isLarge && (
                                            <p className="text-white/80 text-sm line-clamp-2 mb-4">
                                                {item.excerpt}
                                            </p>
                                        )}

                                        <div className="inline-flex items-center text-white/90 text-sm font-medium group-hover:translate-x-1 transition-transform">
                                            Read Article <ArrowUpRight className="w-4 h-4 ml-1" />
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="text-center mt-12">
                    <Link to="/news">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="btn-outline inline-flex items-center space-x-2"
                        >
                            <Newspaper className="w-4 h-4" />
                            <span>View All News</span>
                        </motion.button>
                    </Link>
                </div>
            </div>
        </section>
    );
}
